"use client";

import { AnimatePresence, motion } from "motion/react";
import Link from "next/link";
import { Text, TextGroup } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/core/i18n";
import type { Dictionary } from "@/core/i18n/__generated__/dictionary";
import { useI18n } from "@/core/i18n/useI18n/useI18n";
const STAGGER_DELAY = 0.08;
const TRANSITION = {
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1]
} as const;
function getStaggerProps(index: number) {
  return {
    initial: {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      ...TRANSITION,
      delay: index * STAGGER_DELAY
    }
  };
}
type BodyLink = {
  token: string;
  label: string;
  href: string;
};
function renderBodyWithLinks(body: string, links: BodyLink[]): React.ReactNode[] {
  const tokenPattern = links.map(l => `(\\{${l.token}\\})`).join("|");
  const parts = body.split(new RegExp(tokenPattern));
  const tokenMap = new Map(links.map(l => [`{${l.token}}`, l]));
  return parts.filter(Boolean).map((part, i) => {
    const link = tokenMap.get(part);
    if (link) {
      return <Link className="text-foreground underline hover:no-underline" href={link.href} key={i} rel="noopener noreferrer" target="_blank">
          <Text component="span" text={link.label} variant="caption" />
        </Link>;
    }
    return <Text component="span" key={i} text={part} variant="caption" />;
  });
}
type ConsentItemProp = {
  type: string;
  label: string;
  required: boolean;
};
type ConsentFormProps = {
  consentChecked: boolean;
  isVerifying: boolean;
  onConsentChange: (checked: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
  translationKeys?: Partial<ConsentFormTranslationKeys>;
  biometricConsentItems?: ConsentItemProp[];
};
export function ConsentForm({
  consentChecked,
  isVerifying,
  onConsentChange,
  onCancel,
  onConfirm,
  translationKeys,
  biometricConsentItems
}: ConsentFormProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const {
    currentLang
  } = useI18n();
  const keys = {
    ...DEFAULT_TRANSLATION_KEYS,
    ...translationKeys
  };
  const hasRegionalItems = biometricConsentItems && biometricConsentItems.length > 0;
  const bodyLinks: BodyLink[] = [{
    token: "biometricLink",
    label: t(keys.bodyBiometricLinkLabel),
    href: `/${currentLang}/biometric`
  }, {
    token: "termsLink",
    label: t(keys.bodyTermsLinkLabel),
    href: `/${currentLang}/terms-of-service`
  }];
  let staggerIndex = 0;
  return <AnimatePresence>
      <div className="flex flex-col gap-4">
        <motion.div {...getStaggerProps(staggerIndex++)}>
          <Text align="center" component="h2" text={t(keys.title)} variant="heading-xl" />
        </motion.div>

        {hasRegionalItems ? biometricConsentItems.map(item => <motion.div {...getStaggerProps(staggerIndex++)} key={item.type}>
                <TextGroup align="center" component="p" variant="caption">
                  <Text component="span" text={item.label} variant="caption" />
                </TextGroup>
              </motion.div>) : null}

        <motion.div {...getStaggerProps(staggerIndex++)}>
          <TextGroup align="center" component="p" variant="caption">
            {renderBodyWithLinks(t(keys.body), bodyLinks)}
            {t(keys.highlight) ? <>
                {" "}
                <Text component="span" text={t(keys.highlight)} weight="bold" />
              </> : null}
          </TextGroup>
        </motion.div>

        <motion.label className="bg-foreground-muted/50 relative z-20 flex cursor-pointer items-start gap-3 rounded-lg p-4 leading-tight" data-testid="consent-container" htmlFor="consent" {...getStaggerProps(staggerIndex++)}>
          <Checkbox checked={consentChecked} className="mt-0.5" id="consent" onCheckedChange={checked => onConsentChange(checked === true)} />

          <TextGroup component="span" variant="caption">
            <Text component="span" text={t(keys.checkboxPrefix)} variant="caption" />
            {t(keys.privacyLink) ? <>
                {" "}
                <Link className="text-foreground underline hover:no-underline" href="/privacy" rel="noopener noreferrer" target="_blank">
                  <Text component="span" text={t(keys.privacyLink)} variant="caption" />
                </Link>
              </> : null}
            {t(keys.checkboxSuffix) ? <Text component="span" text={t(keys.checkboxSuffix)} variant="caption" /> : null}
            {t(keys.checkboxDeidentified) ? <Text component="span" text={t(keys.checkboxDeidentified)} variant="caption" /> : null}
          </TextGroup>
        </motion.label>

        {t(keys.termsPrefix) ? <motion.div className="text-foreground text-center" {...getStaggerProps(staggerIndex++)}>
            <Text component="span" text={t(keys.termsPrefix)} variant="caption" />{" "}
            <Link className="text-foreground inline underline hover:no-underline" href="/terms-of-service" rel="noopener noreferrer" target="_blank">
              <Text component="span" text={t(keys.termsLink)} variant="caption" />
            </Link>
            <Text component="span" text={t(keys.termsSuffix)} variant="caption" />
          </motion.div> : null}

        <motion.div className="flex gap-3" {...getStaggerProps(staggerIndex++)}>
          <Button className="flex-1" disabled={isVerifying} variant="outline" onClick={onCancel}>
            <Text component="span" text={t(keys.cancel)} />
          </Button>
          <Button className="flex-1" disabled={!consentChecked || isVerifying} onClick={onConfirm}>
            <Text component="span" text={t(keys.confirm)} />
          </Button>
        </motion.div>
      </div>
    </AnimatePresence>;
}
export type ConsentFormTranslationKeys = {
  title: keyof Dictionary;
  body: keyof Dictionary;
  bodyBiometricLinkLabel: keyof Dictionary;
  bodyTermsLinkLabel: keyof Dictionary;
  highlight: keyof Dictionary;
  checkboxPrefix: keyof Dictionary;
  checkboxSuffix: keyof Dictionary;
  checkboxDeidentified: keyof Dictionary;
  privacyLink: keyof Dictionary;
  termsPrefix: keyof Dictionary;
  termsLink: keyof Dictionary;
  termsSuffix: keyof Dictionary;
  cancel: keyof Dictionary;
  confirm: keyof Dictionary;
};
const DEFAULT_TRANSLATION_KEYS: ConsentFormTranslationKeys = {
  title: "assessmentSteps.studioSession.consent.title",
  body: "assessmentSteps.studioSession.consent.body",
  bodyBiometricLinkLabel: "assessmentSteps.studioSession.consent.bodyBiometricLinkLabel",
  bodyTermsLinkLabel: "assessmentSteps.studioSession.consent.bodyTermsLinkLabel",
  highlight: "assessmentSteps.studioSession.consent.highlight",
  checkboxPrefix: "assessmentSteps.studioSession.consent.checkboxPrefix",
  checkboxSuffix: "assessmentSteps.studioSession.consent.checkboxSuffix",
  checkboxDeidentified: "assessmentSteps.studioSession.consent.checkboxDeidentified",
  privacyLink: "assessmentSteps.studioSession.consent.privacyLink",
  termsPrefix: "assessmentSteps.studioSession.consent.termsPrefix",
  termsLink: "assessmentSteps.studioSession.consent.termsLink",
  termsSuffix: "assessmentSteps.studioSession.consent.termsSuffix",
  cancel: "assessmentSteps.studioSession.consent.cancel",
  confirm: "assessmentSteps.studioSession.consent.confirm"
};